/**
 * @flow
 */

import React from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { useMutation } from '@apollo/client';
import { useApolloClient } from '@apollo/client';
import { uniqBy, range, find } from 'lodash';
import { format, isAfter, startOfDay, addMonths, startOfMonth } from 'date-fns';
import { enUS, ru, lv } from 'date-fns/locale';
import { DropTarget } from 'react-dnd';
import gql from 'graphql-tag';

import { Cell } from '../../../../components/timetable';
import {
    AppointmentCardContainer,
    AppointmentCell,
} from '../../../appointments';
import {
    CELL_HEIGHT_PX,
    CELL_STEP,
} from '../../../../components/timetable/Cell';
import { offsetToTime } from '../../../../lib/schedule';
import { GET_MASTER_APPOINTMENTS_QUERY } from '../../containers/queries';
import { GET_WORKING_DAYS } from '../DateRange';

import type { ScheduleSettings, Salon, Master } from '../../../../type';
import type { TimetableDay } from '../AppointmentTable';

const UPDATE_APPOINTMENT_QUERY = gql`
    mutation updateAppointment($input: UpdateAppointmentInput!) {
        updateAppointment(input: $input) {
            appointment {
                id
                master {
                    id
                }
                startAt
                endAt
            }
        }
    }
`;

const ColumnWrapper = styled.div`
    border-right: 1px solid #e9e9e9;
    flex: 0 0 auto;
    height: 100%;
    display: flex;
    flex-direction: column;
`;

const ColumnHeader = styled(Cell)`
    padding: 0px 14px;
    cursor: pointer;
    transition: background 0.3s, color 0.3s;
    text-align: center;
    text-transform: capitalize;

    &:hover {
        color: #35a3d5;
        background: #eef8fc;
    }
`;

const ColumnContent = styled.div`
    position: relative;
    flex: 1;
    display: flex;
    flex-direction: column;
    & .cell:last-child {
        border-bottom: 0px !important;
    }
`;

type AppointmentColumnInnerProps = {
    connectDropTarget: Function,
    schedule: ScheduleSettings,
    master: Master,
    day: TimetableDay,
    salon: Salon,
    timeSpans: Array<number>,
    allowToAddAppointments: Function,
    locale: string,
    fromDate: mixed,
    toDate: mixed,
    cellHeight: number,
    onDrop: Function,
    updateAppointmentClient: Function,
};

/**
 * Pure presentational inner component — all data comes from props so that
 * react-dnd's DropTarget can access `onDrop`, `cellHeight`, etc. via `props`
 * in the `target.drop` spec function.
 */
const AppointmentColumnInner = ({
    connectDropTarget,
    schedule,
    master,
    day,
    salon,
    timeSpans,
    allowToAddAppointments,
    locale,
    fromDate,
    toDate,
    updateAppointmentClient,
}: AppointmentColumnInnerProps) =>
    connectDropTarget(
        <div style={{ flex: 1 }}>
            <ColumnWrapper>
                <ColumnHeader>
                    {format(new Date(day.date), 'EEEE, d MMM', {
                        locale: { en: enUS, ru, lv }[locale] || enUS,
                    })}
                </ColumnHeader>
                <ColumnContent>
                    {timeSpans.map(time => (
                        <AppointmentCell
                            key={time}
                            salon={salon}
                            date={day.date}
                            time={time}
                            schedule={schedule}
                            provider={{
                                id: master.id,
                                schedule: day.schedule,
                            }}
                            allowToAddAppointments={allowToAddAppointments(
                                day.date,
                            )}
                            masterAppointments={true}
                            fromDate={fromDate}
                            toDate={toDate}
                        />
                    ))}
                    <AppointmentCardContainer
                        appointments={uniqBy(day.appointments, 'id')}
                        salon={salon}
                        updateAppointmentClient={updateAppointmentClient}
                        masterAppointments={true}
                        fromDate={fromDate}
                        toDate={toDate}
                    />
                </ColumnContent>
            </ColumnWrapper>
        </div>,
    );

// Specify the drop target contract.
// `props` here are the props of AppointmentColumnInner (passed from the outer
// AppointmentColumn component), which includes onDrop and cellHeight.
const target = {
    drop: (props, monitor) => {
        const { appointment } = monitor.getItem();
        const time = offsetToTime(
            `${props.day.date} ${format(new Date(appointment.startAt), 'HH:mm')}`,
            props.cellHeight,
            CELL_STEP,
            monitor.getDifferenceFromInitialOffset().y,
            props.schedule.step,
        );
        props.onDrop({
            appointment: appointment,
            master: props.master,
            startAt: time,
        });
    },
};

const collect = (connect) => ({
    connectDropTarget: connect.dropTarget(),
});

const AppointmentColumnWithDrop = DropTarget(
    'APPOINTMENT',
    target,
    collect,
)(AppointmentColumnInner);

/**
 * Outer component — uses hooks to read from redux/apollo and defines
 * the onDrop mutation handler, then passes everything as props to the
 * DropTarget-wrapped inner component.
 */
function AppointmentColumn(props: {
    schedule: ScheduleSettings,
    master: Master,
    day: TimetableDay,
    salon: Salon,
    updateAppointmentClient: Function,
}) {
    const apolloClient = useApolloClient();
    const locale = useSelector(({ intl }) => intl.get('locale'));
    const fromDate = useSelector(({ masterAppointments }) =>
        masterAppointments.get('fromDate'),
    );
    const toDate = useSelector(({ masterAppointments }) =>
        masterAppointments.get('toDate'),
    );
    const isOwner = useSelector(({ user }) => user.get('isOwner'));
    const cellHeight = useSelector(
        ({ appointments }) => appointments.get('cellHeight') || CELL_HEIGHT_PX,
    );
    const showArchive = useSelector(({ salon: salonState }) =>
        salonState.get('showArchive'),
    );
    const currentLanguage = useSelector(
        ({ intl }) => intl.get('locale') || intl.get('defaultLanguage'),
    );

    const [updateAppointment] = useMutation(UPDATE_APPOINTMENT_QUERY);

    const timeSpans = range(props.schedule.startAt, props.schedule.endAt, CELL_STEP);
    const allowToAddAppointments = date =>
        isOwner || !isAfter(startOfDay(new Date()), startOfDay(new Date(date)));

    const onDrop = data => {
        const paid = data.appointment.invoice
            ? data.appointment.invoice.paid
            : null;
        if (!paid) {
            return updateAppointment({
                variables: {
                    input: {
                        appointmentId: data.appointment.id,
                        master: data.master.id,
                        client: data.appointment.client.id,
                        startAt: format(new Date(data.startAt), 'yyyy-MM-dd HH:mm'),
                    },
                },
                update: (store, { data: { updateAppointment: result } }) => {
                    const ownAppointment = find(
                        props.master.appointments,
                        item => item.id === result.appointment.id,
                    );

                    // Update master appointments cache
                    const maVars = {
                        master: result.appointment.master.id,
                        salon: props.salon.id,
                        fromDate: format(new Date(fromDate), 'yyyy-MM-dd'),
                        toDate: format(new Date(toDate), 'yyyy-MM-dd'),
                        withMaster: true,
                        showArchive,
                        lang: currentLanguage,
                    };
                    const maData = store.readQuery({
                        query: GET_MASTER_APPOINTMENTS_QUERY,
                        variables: maVars,
                    });
                    if (maData) {
                        const maAppointments =
                            maData.viewer.salon.master.appointments || [];
                        const nextMaAppointments = maAppointments.map(item => {
                            if (item.id === result.appointment.id) {
                                return {
                                    ...item,
                                    endAt: result.appointment.endAt,
                                    startAt: result.appointment.startAt,
                                };
                            }
                            return item;
                        });
                        store.writeQuery({
                            query: GET_MASTER_APPOINTMENTS_QUERY,
                            variables: maVars,
                            data: {
                                ...maData,
                                viewer: {
                                    ...maData.viewer,
                                    salon: {
                                        ...maData.viewer.salon,
                                        master: {
                                            ...maData.viewer.salon.master,
                                            appointments: nextMaAppointments,
                                        },
                                    },
                                },
                            },
                        });
                    }

                    // Update working days cache
                    const wdFromDate = format(new Date(), 'yyyy-MM-dd');
                    const wdToDate = format(addMonths(startOfMonth(new Date()), 6), 'yyyy-MM-dd');
                    const wdVars = {
                        salon: props.salon.id,
                        master: result.appointment.master.id,
                        fromDate: wdFromDate,
                        toDate: wdToDate,
                    };
                    const wdData = store.readQuery({
                        query: GET_WORKING_DAYS,
                        variables: wdVars,
                    });
                    if (wdData && ownAppointment) {
                        const workingDays =
                            wdData.viewer.salon.workingDays || [];
                        const oldDateStr = format(new Date(ownAppointment.startAt), 'yyyy-MM-dd');
                        const newDateStr = format(new Date(result.appointment.startAt), 'yyyy-MM-dd');
                        const hasNewDate = workingDays.some(
                            item => item.date === newDateStr,
                        );
                        let nextWorkingDays = workingDays;
                        if (hasNewDate) {
                            if (newDateStr !== oldDateStr) {
                                nextWorkingDays = workingDays
                                    .map(item => {
                                        if (item.date === oldDateStr) {
                                            return {
                                                ...item,
                                                appointmentCount:
                                                    item.appointmentCount - 1,
                                            };
                                        }
                                        if (item.date === newDateStr) {
                                            return {
                                                ...item,
                                                appointmentCount:
                                                    item.appointmentCount + 1,
                                            };
                                        }
                                        return item;
                                    })
                                    .filter(item => item.appointmentCount > 0);
                            }
                        } else {
                            const filtered = workingDays
                                .map(item => {
                                    if (item.date === oldDateStr) {
                                        return {
                                            ...item,
                                            appointmentCount:
                                                item.appointmentCount - 1,
                                        };
                                    }
                                    return item;
                                })
                                .filter(item => item.appointmentCount > 0);
                            nextWorkingDays = [
                                ...filtered,
                                {
                                    date: newDateStr,
                                    appointmentCount: 1,
                                    __typename: 'WorkingDay',
                                },
                            ];
                        }
                        store.writeQuery({
                            query: GET_WORKING_DAYS,
                            variables: wdVars,
                            data: {
                                ...wdData,
                                viewer: {
                                    ...wdData.viewer,
                                    salon: {
                                        ...wdData.viewer.salon,
                                        workingDays: nextWorkingDays,
                                    },
                                },
                            },
                        });
                    }
                },
            });
        }
    };

    return (
        <AppointmentColumnWithDrop
            {...props}
            locale={locale}
            fromDate={fromDate}
            toDate={toDate}
            cellHeight={cellHeight}
            timeSpans={timeSpans}
            allowToAddAppointments={allowToAddAppointments}
            onDrop={onDrop}
        />
    );
}

export default AppointmentColumn;
